import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Target, Clock, Battery, Info } from 'lucide-react';
import { WorkoutRecommendation, getWorkoutRecommendations } from '../../utils/workoutRecommendations';
import { HealthMetrics } from '../../types/health';

interface WorkoutRecommendationsProps {
  metrics: HealthMetrics;
  onSelectWorkout: (workout: WorkoutRecommendation) => void;
}

export function WorkoutRecommendations({ metrics, onSelectWorkout }: WorkoutRecommendationsProps) {
  const recommendations = getWorkoutRecommendations(metrics);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Target className="w-6 h-6 text-primary" />
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Recommended Workouts</h2>
            <p className="text-sm text-muted-foreground">Based on your current metrics</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Battery className="w-4 h-4 text-primary" />
          <span className="text-primary">{metrics.recovery}% Recovery</span>
        </div>
      </div>

      {/* Recommendations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {recommendations.map((workout, index) => (
          <motion.div
            key={workout.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`theme-card p-4 relative overflow-hidden cursor-pointer transition-all ${
              workout.suitable 
                ? 'hover:ring-1 hover:ring-primary/50' 
                : 'opacity-50 cursor-not-allowed'
            }`}
            onClick={() => workout.suitable && onSelectWorkout(workout)}
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />

            {/* Content */}
            <div className="relative space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-primary" />
                  <div>
                    <h3 className="font-medium">{workout.name}</h3>
                    <p className="text-sm text-muted-foreground">{workout.intensity} Intensity</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{workout.targetStrain.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground">Target Strain</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>{workout.duration} min</span>
                </div>
                <div className="flex items-center gap-2">
                  <Battery className="w-4 h-4 text-primary" />
                  <span>{workout.recoveryRequired}h recovery</span>
                </div>
              </div>

              {!workout.suitable && (
                <div className="flex items-center gap-2 text-xs text-warning">
                  <Info className="w-4 h-4" />
                  <span>{workout.reason}</span>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                {workout.benefits[0]}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}